import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save } from 'lucide-react';
import { Section, Field, Modal, ImageUpload } from './_shared';

const empty = { name: '', role: '', arabic: '', photo_base64: '', linkedin: '', bio: '', order: 0, group: 'management' };

export default function TeamPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await cx.get('/team'); setItems(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (editing.id) await cx.put(`/team/${editing.id}`, payload);
      else await cx.post('/team', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this member?')) return;
    try { await cx.delete(`/team/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const management = items.filter((m) => m.group === 'management');
  const advisory = items.filter((m) => m.group === 'advisory');

  return (
    <Section
      title="Team & Advisory"
      action={<button data-testid="admin-team-add" onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus size={14} /> Add member</button>}
    >
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : (
        <div className="space-y-8">
          <Group title="Management" data={management} onEdit={setEditing} onDelete={del} />
          <Group title="Advisory Board" data={advisory} onEdit={setEditing} onDelete={del} />
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit member' : 'Add member'}>
          <div className="space-y-4">
            <ImageUpload value={editing.photo_base64} onChange={(v) => setEditing({ ...editing, photo_base64: v })} label="Upload photo" />
            <Field label="Name"><input className="lux-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="Role / Title"><input className="lux-input" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} /></Field>
            <Field label="Arabic accent (optional)"><input className="lux-input font-arabic" value={editing.arabic} onChange={(e) => setEditing({ ...editing, arabic: e.target.value })} /></Field>
            <Field label="LinkedIn URL"><input className="lux-input" value={editing.linkedin} onChange={(e) => setEditing({ ...editing, linkedin: e.target.value })} /></Field>
            <Field label="Short bio"><textarea rows={3} className="lux-input resize-none" value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Group">
                <select className="lux-input" value={editing.group} onChange={(e) => setEditing({ ...editing, group: e.target.value })}>
                  <option value="management">Management</option>
                  <option value="advisory">Advisory</option>
                </select>
              </Field>
              <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-team-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}

function Group({ title, data, onEdit, onDelete }) {
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      {data.length === 0 ? <p className="text-muted text-sm">None yet.</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((m) => (
            <div key={m.id} className="border border-subtle surface rounded-md p-4 flex gap-3">
              <div className="w-16 h-16 rounded-md surface-2 overflow-hidden shrink-0 flex items-center justify-center">
                {m.photo_base64 ? <img src={m.photo_base64} alt={m.name} className="photo-fit" /> : <span className="font-display text-xl text-muted">{m.name?.[0]}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm text-prim truncate">{m.name}</p>
                <p className="text-xs text-muted truncate">{m.role}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => onEdit(m)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={11} /> Edit</button>
                  <button onClick={() => onDelete(m.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={11} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
